/**
 * APP BLOG COMPONENT
 * ==================
 * 
 * In-app version of the blog component for authenticated users.
 * Provides blog content within the logged-in experience.
 */

import { useState, useMemo } from "react";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { ArrowLeft, Search, Calendar, Clock, User, Tag } from "lucide-react";
import { getBlogCategories, getBlogTags, searchBlogPosts, type BlogPostData } from "../src/lib/blog-loader";
import { BlogPost } from "./BlogPost";
import { NewsletterForm } from "./NewsletterForm";

interface AppBlogProps {
  onBack: () => void;
}

type BlogView = 'list' | 'post';

export function AppBlog({ onBack }: AppBlogProps) {
  const [currentView, setCurrentView] = useState<BlogView>('list');
  const [selectedPost, setSelectedPost] = useState<BlogPostData | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedTag, setSelectedTag] = useState<string>('');

  // Navigation handlers
  const handlePostClick = (post: BlogPostData) => {
    setSelectedPost(post);
    setCurrentView('post');
  };

  const handleBackToBlog = () => {
    setCurrentView('list');
    setSelectedPost(null);
  };

  // Data loading
  const categories = getBlogCategories();
  const tags = getBlogTags();

  // Filter posts based on search and filters
  const filteredPosts = useMemo(() => {
    return searchBlogPosts(searchQuery, selectedCategory || undefined, selectedTag || undefined);
  }, [searchQuery, selectedCategory, selectedTag]);

  // Utility functions
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('');
    setSelectedTag('');
  };

  // If viewing a single post, render the BlogPost component
  if (currentView === 'post' && selectedPost) {
    return (
      <BlogPost 
        post={selectedPost}
        onBackToBlog={handleBackToBlog}
      />
    );
  }

  // Render blog list view
  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-4 mb-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onBack}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Dashboard</span>
          </Button>
        </div>
        <h1 className="text-3xl font-semibold mb-2">Decision-Making Blog</h1>
        <p className="text-muted-foreground">
          Insights, strategies, and research on effective decision-making
        </p>
      </div>

      {/* Search and Filters */}
      <div className="mb-8 space-y-4">
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search articles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant={selectedCategory === '' ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedCategory('')}
          >
            All Categories
          </Button>
          {categories.map(category => (
            <Button
              key={category}
              variant={selectedCategory === category ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </Button>
          ))}
        </div>

        {/* Tag Filters */}
        <div className="flex flex-wrap gap-2">
          {tags.slice(0, 10).map(tag => (
            <Badge
              key={tag}
              variant={selectedTag === tag ? "default" : "secondary"}
              className="cursor-pointer hover:bg-primary/80"
              onClick={() => setSelectedTag(selectedTag === tag ? '' : tag)}
            >
              <Tag className="h-3 w-3 mr-1" />
              {tag}
            </Badge>
          ))}
        </div>

        {/* Clear Filters */}
        {(searchQuery || selectedCategory || selectedTag) && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            Clear all filters
          </Button>
        )}
      </div>

      {/* Blog Posts Grid */}
      {filteredPosts.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPosts.map((post) => (
            <Card 
              key={post.id} 
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => handlePostClick(post)}
            >
              {post.featuredImage && (
                <div className="aspect-video bg-muted overflow-hidden rounded-t-lg">
                  <img 
                    src={post.featuredImage} 
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <CardHeader>
                <div className="flex flex-wrap gap-1 mb-2">
                  {post.tags.slice(0, 2).map(tag => (
                    <Badge key={tag} variant="secondary" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
                <CardTitle className="text-lg leading-tight">{post.title}</CardTitle>
                <CardDescription>{post.excerpt}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-3 w-3" />
                      <span>{formatDate(post.date)}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Clock className="h-3 w-3" />
                      <span>{post.readTime}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-1 mt-2 text-sm text-muted-foreground">
                  <User className="h-3 w-3" />
                  <span>{post.author}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <h3 className="text-lg font-medium mb-2">No articles found</h3>
          <p className="text-muted-foreground mb-4">
            Try adjusting your search terms or filters
          </p>
          <Button variant="outline" onClick={clearFilters}>
            Clear filters
          </Button>
        </div>
      )}

      {/* Newsletter Signup */}
      <div className="mt-16">
        <NewsletterForm 
          title="Stay Updated on Decision Science"
          description="Get the latest insights on decision-making research, frameworks, and practical tips delivered to your inbox."
        />
      </div>
    </div>
  );
}